import { useState, useEffect, useRef, useCallback } from "react";
import { useTheme, DARK, LIGHT } from "../../components/Layout";
import { chatAPI } from "../../services/api";
import PersianDatePicker, { gregorianToJalaliStr, getTodayJalali } from "../../utils/PersianDatePicker";
import {
  MessageCircle, Users, Plus, Search, Send, Paperclip, Mic,
  X, Edit2, Trash2, UserPlus, LogOut, Hash, Check, CheckCheck,
  Calendar, Bell, Clock, ChevronLeft, ChevronRight, Settings,
  Download, Play, Pause, Pin, MoreVertical, Camera,
} from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────
interface User    { id: number; name: string; username: string; avatar?: string; is_online?: boolean; last_seen?: string; }
interface Member  { id: number; name: string; username: string; avatar?: string; is_online?: boolean; room_role?: string; }
interface Room    { id: number; name: string; type: "direct"|"group"; avatar?: string; description?: string; created_by: number; members: Member[]; last_message?: Message; unread_count: number; }
interface Message { id: number; room_id: number; sender_id: number; sender_name: string; sender_avatar?: string; content: string; type: "text"|"file"|"voice"|"image"; file_url?: string; file_name?: string; file_size?: number; duration?: number; created_at: string; is_read: boolean; is_pinned?: boolean; reply_to?: number; }
interface Alarm   { id: number; title: string; date: string; time: string; note?: string; sound?: string; repeat_type?: string; is_done?: boolean; shared_with?: string; }
interface CalEvent{ id: number; title: string; date: string; time?: string; color: string; type: "event"|"task"|"reminder"; done?: boolean; note?: string; shared_with?: string; }

// ─── Jalali helpers ───────────────────────────────────────────────────────────
const JALALI_MONTHS = ["حمل","ثور","جوزا","سرطان","اسد","سنبله","میزان","عقرب","قوس","جدی","دلو","حوت"];
const JALALI_DAYS   = ["ش","ی","د","س","چ","پ","ج"];

function toJalali(gy:number,gm:number,gd:number):[number,number,number]{const g_d_no=[0,31,(gy%4===0&&gy%100!==0||gy%400===0)?29:28,31,30,31,30,31,31,30,31,30,31];let jy=gy<=1600?(gy-621):(gy-1600);let jm=0,jd=0;const g_y2=gy-((gm<=2)?1:0);let gy2=g_y2-1600;let gd2=Math.floor(g_y2/4)-Math.floor(g_y2/100)+Math.floor(g_y2/400)-Math.floor(1600/4)+Math.floor(1600/100)-Math.floor(1600/400);let gd3=0;for(let i=0;i<gm-1;i++)gd3+=g_d_no[i+1];gd3+=gd-1;let g_day_no=365*gy2+gd2+gd3;let j_day_no=g_day_no-79;let j_np=Math.floor(j_day_no/12053);j_day_no%=12053;jy+=33*j_np+4*Math.floor(j_day_no/1461);j_day_no%=1461;if(j_day_no>=366){jy+=Math.floor((j_day_no-1)/365);j_day_no=(j_day_no-1)%365;}const j_mi=[0,31,60,91,121,152,182,213,244,274,305,335];for(let i=11;i>=0;i--){if(j_day_no>=j_mi[i]){jm=i+1;jd=j_day_no-j_mi[i]+1;break;}}return[jy,jm,jd];}
function getJalaliDaysInMonth(jy:number,jm:number):number{if(jm<=6)return 31;if(jm<=11)return 30;const[gy,gm]=toJalali(jy,jm,1).map((v,i)=>i===0?v:v);const d=new Date(gy,gm-1,29);d.setDate(d.getDate()+1);return d.getDate()===1?29:30;}
function getFirstDayOfWeek(jy:number,jm:number):number{const t=getTodayJalali();const[gy,gm,]=toJalali(jy,jm,1);const day=new Date(gy,gm-1,1).getDay();return(day+1)%7;}

// ─── Avatar ───────────────────────────────────────────────────────────────────
const COLORS = ["#10b981","#3b82f6","#8b5cf6","#f59e0b","#ef4444","#06b6d4","#ec4899","#84cc16"];
const API_URL = "https://www.hamidigroupfinance.com";

const Avatar = ({ name, avatar, size=36, online }: { name:string; avatar?:string|null; size?:number; online?:boolean }) => {
  const c = COLORS[(name?.charCodeAt(0)||0) % COLORS.length];
  const initials = (name||"?").split(" ").slice(0,2).map(w=>w[0]||"").join("").toUpperCase();
  const src = avatar ? (avatar.startsWith("http") ? avatar : `${API_URL}${avatar}`) : null;
  return (
    <div style={{ position:"relative", flexShrink:0, width:size, height:size }}>
      {src
        ? <img src={src} alt={name} style={{ width:size, height:size, borderRadius:"50%", objectFit:"cover" }} onError={e=>{(e.target as HTMLImageElement).style.display="none";}} />
        : <div style={{ width:size, height:size, borderRadius:"50%", background:c, display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontSize:size*0.36, fontWeight:700 }}>{initials}</div>
      }
      {online !== undefined && (
        <span style={{ position:"absolute", bottom:1, right:1, width:size*0.28, height:size*0.28, borderRadius:"50%", background:online?"#10b981":"#94a3b8", border:"2px solid white" }} />
      )}
    </div>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function ChatPage() {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;

  // Colors
  const bg     = dark ? "#0f172a" : "#f1f5f9";
  const panel  = dark ? "#1e293b" : "#ffffff";
  const panel2 = dark ? "#162032" : "#f8fafc";
  const border = dark ? "#334155" : "#e2e8f0";
  const accent = "#6366f1";

  // State - chat
  const [tab, setTab]               = useState<"chat"|"calendar">("chat");
  const [rooms, setRooms]           = useState<Room[]>([]);
  const [users, setUsers]           = useState<User[]>([]);
  const [activeRoom, setActiveRoom] = useState<Room|null>(null);
  const [messages, setMessages]     = useState<Message[]>([]);
  const [input, setInput]           = useState("");
  const [search, setSearch]         = useState("");
  const [replyTo, setReplyTo]       = useState<Message|null>(null);
  const [loading, setLoading]       = useState(false);
  const [msgLoading, setMsgLoading] = useState(false);
  const [toast, setToast]           = useState({ text:"", ok:true });

  // State - modals
  const [showNewRoom, setShowNewRoom]     = useState(false);
  const [showRoomInfo, setShowRoomInfo]   = useState(false);
  const [showProfile, setShowProfile]     = useState(false);
  const [showAddEvent, setShowAddEvent]   = useState(false);
  const [showAddAlarm, setShowAddAlarm]   = useState(false);

  // State - forms
  const [newRoomForm, setNewRoomForm] = useState({ name:"", type:"group" as "direct"|"group", members:[] as number[], description:"" });
  const [profileForm, setProfileForm] = useState({ full_name:"", avatar:"" });
  const [myProfile, setMyProfile]     = useState<any>(null);
  const [profileUploading, setProfileUploading] = useState(false);

  // State - calendar
  const jToday = getTodayJalali();
  const [calYear, setCalYear]       = useState(jToday.jy);
  const [calMonth, setCalMonth]     = useState(jToday.jm);
  const [selectedDay, setSelectedDay] = useState<number|null>(jToday.jd);
  const [events, setEvents]         = useState<CalEvent[]>([]);
  const [alarms, setAlarms]         = useState<Alarm[]>([]);
  const [activeAlarm, setActiveAlarm] = useState<Alarm|null>(null);
  const [eventForm, setEventForm]   = useState({ title:"", time:"", color:"#3b82f6", type:"event" as CalEvent["type"], note:"", shared_with:[] as number[] });
  const [alarmForm, setAlarmForm]   = useState({ title:"", date:"", time:"", note:"", sound:"default", repeat:"none", shared_with:[] as number[] });

  // State - voice
  const [recording, setRecording]         = useState(false);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const [playingVoice, setPlayingVoice]   = useState<number|null>(null);

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef   = useRef<HTMLInputElement>(null);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const mediaRecorder  = useRef<MediaRecorder|null>(null);
  const audioChunks    = useRef<Blob[]>([]);
  const pollRef        = useRef<any>(null);
  const alarmRef       = useRef<any>(null);
  const recTimerRef    = useRef<any>(null);
  const audioRefs      = useRef<Record<number, HTMLAudioElement>>({});

  const myId = parseInt(localStorage.getItem("user_id") || "0");

  const showToast = (text:string, ok=true) => { setToast({text,ok}); setTimeout(()=>setToast({text:"",ok:true}),3000); };

  // ── Load profile ──────────────────────────────────────────────────────────
  useEffect(() => {
    chatAPI.getProfile().then((r:any) => {
      if (r.success) { setMyProfile(r.user); setProfileForm({ full_name: r.user.full_name||"", avatar: r.user.avatar||"" }); }
    });
  }, []);

  // ── Load rooms & users ────────────────────────────────────────────────────
  const loadRooms = useCallback(async () => {
    const [r1, r2] = await Promise.all([chatAPI.getRooms(), chatAPI.getUsers()]);
    if (r1.success) setRooms(r1.rooms || []);
    if (r2.success) setUsers(r2.users || []);
  }, []);

  useEffect(() => { loadRooms(); }, [loadRooms]);

  // ── Poll messages ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!activeRoom) return;
    const poll = async () => {
      const lastId = messages[messages.length-1]?.id || 0;
      const res = await chatAPI.getMessages(activeRoom.id, lastId);
      if (res.success && res.messages?.length > 0) {
        setMessages(prev => {
          const ids = new Set(prev.map((m:Message)=>m.id));
          return [...prev, ...res.messages.filter((m:Message)=>!ids.has(m.id))];
        });
      }
    };
    pollRef.current = setInterval(poll, 3000);
    return () => clearInterval(pollRef.current);
  }, [activeRoom, messages]);

  // ── Scroll to bottom ──────────────────────────────────────────────────────
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior:"smooth" }); }, [messages]);

  // ── Alarm checker ─────────────────────────────────────────────────────────
  useEffect(() => {
    alarmRef.current = setInterval(() => {
      const now  = new Date();
      const jNow = getTodayJalali();
      const dateStr = `${jNow.jy}/${String(jNow.jm).padStart(2,"0")}/${String(jNow.jd).padStart(2,"0")}`;
      const timeStr = `${String(now.getHours()).padStart(2,"0")}:${String(now.getMinutes()).padStart(2,"0")}`;
      const alarm = alarms.find(a => a.date === dateStr && a.time === timeStr && !a.is_done);
      if (alarm) setActiveAlarm(alarm);
    }, 30000);
    return () => clearInterval(alarmRef.current);
  }, [alarms]);

  // ── Load calendar data ────────────────────────────────────────────────────
  const loadCalendarData = useCallback(async () => {
    const [r1, r2] = await Promise.all([chatAPI.getAlarms(), chatAPI.getEvents()]);
    if (r1.success) setAlarms(r1.alarms || []);
    if (r2.success) setEvents(r2.events || []);
  }, []);

  useEffect(() => { if (tab === "calendar") loadCalendarData(); }, [tab, loadCalendarData]);

  // ── Open room ─────────────────────────────────────────────────────────────
  const openRoom = async (room: Room) => {
    setActiveRoom(room);
    setMsgLoading(true);
    setMessages([]);
    const res = await chatAPI.getMessages(room.id);
    if (res.success) setMessages(res.messages || []);
    setMsgLoading(false);
    chatAPI.markRead(room.id);
    setRooms(prev => prev.map(r => r.id === room.id ? {...r, unread_count:0} : r));
  };

  // ── Send message ──────────────────────────────────────────────────────────
  const sendMessage = async () => {
    if (!input.trim() || !activeRoom) return;
    const tmp: Message = { id: Date.now(), room_id: activeRoom.id, sender_id: myId, sender_name: myProfile?.full_name||"من", content: input, type:"text", created_at: new Date().toISOString(), is_read: false, reply_to: replyTo?.id };
    setMessages(prev => [...prev, tmp]);
    setInput("");
    setReplyTo(null);
    const res = await chatAPI.sendMessage({ room_id: activeRoom.id, content: input, type:"text", reply_to: replyTo?.id });
    if (!res.success) showToast("خطا در ارسال پیام", false);
    loadRooms();
  };

  // ── Send file ─────────────────────────────────────────────────────────────
  const sendFile = async (file: File) => {
    if (!activeRoom) return;
    showToast("در حال آپلود...");
    const up = await chatAPI.uploadFile(file);
    if (!up.success) { showToast("خطا در آپلود", false); return; }
    const isImage = file.type.startsWith("image/");
    await chatAPI.sendMessage({ room_id: activeRoom.id, content: file.name, type: isImage ? "image" : "file", file_url: up.url, file_name: file.name, file_size: file.size });
    const res = await chatAPI.getMessages(activeRoom.id);
    if (res.success) setMessages(res.messages || []);
    showToast("فایل ارسال شد");
    loadRooms();
  };

  // ── Voice recording ───────────────────────────────────────────────────────
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorder.current = new MediaRecorder(stream);
      audioChunks.current = [];
      mediaRecorder.current.ondataavailable = e => audioChunks.current.push(e.data);
      mediaRecorder.current.onstop = async () => {
        const blob = new Blob(audioChunks.current, { type:"audio/webm" });
        const file = new File([blob], `voice_${Date.now()}.webm`, { type:"audio/webm" });
        stream.getTracks().forEach(t => t.stop());
        if (!activeRoom) return;
        const up = await chatAPI.uploadFile(file);
        if (up.success) {
          await chatAPI.sendMessage({ room_id: activeRoom.id, content:"پیام صوتی", type:"voice", file_url: up.url, duration: recordSeconds });
          const res = await chatAPI.getMessages(activeRoom.id);
          if (res.success) setMessages(res.messages || []);
        }
      };
      mediaRecorder.current.start();
      setRecording(true);
      setRecordSeconds(0);
      recTimerRef.current = setInterval(() => setRecordSeconds(s => s+1), 1000);
    } catch { showToast("دسترسی به میکروفون رد شد", false); }
  };

  const stopRecording = () => {
    mediaRecorder.current?.stop();
    clearInterval(recTimerRef.current);
    setRecording(false);
    setRecordSeconds(0);
  };

  // ── Create room ───────────────────────────────────────────────────────────
  const createRoom = async () => {
    if (newRoomForm.type === "group" && !newRoomForm.name.trim()) { showToast("نام گروه الزامی است", false); return; }
    if (newRoomForm.type === "direct" && newRoomForm.members.length !== 1) { showToast("یک کاربر انتخاب کنید", false); return; }
    const res = await chatAPI.createRoom(newRoomForm);
    if (res.success) {
      showToast("گفتگو ساخته شد");
      setShowNewRoom(false);
      setNewRoomForm({ name:"", type:"group", members:[], description:"" });
      await loadRooms();
      const allRooms = await chatAPI.getRooms();
      if (allRooms.success) {
        const newRoom = (allRooms.rooms||[]).find((r:Room) => r.id === res.room_id);
        if (newRoom) openRoom(newRoom);
      }
    } else showToast(res.message || "خطا", false);
  };

  // ── Save event ────────────────────────────────────────────────────────────
  const saveEvent = async () => {
    if (!eventForm.title.trim()) { showToast("عنوان الزامی است", false); return; }
    const jd = selectedDay || 1;
    const dateStr = `${calYear}/${String(calMonth).padStart(2,"0")}/${String(jd).padStart(2,"0")}`;
    const res = await chatAPI.addEvent({ ...eventForm, date: dateStr, shared_with: eventForm.shared_with });
    if (res.success) {
      await loadCalendarData();
      setShowAddEvent(false);
      setEventForm({ title:"", time:"", color:"#3b82f6", type:"event", note:"", shared_with:[] });
      showToast("رویداد ثبت شد");
    }
  };

  // ── Save alarm ────────────────────────────────────────────────────────────
  const saveAlarm = async () => {
    if (!alarmForm.title || !alarmForm.date || !alarmForm.time) { showToast("عنوان، تاریخ و ساعت الزامی است", false); return; }
    const res = await chatAPI.setAlarm({ ...alarmForm, shared_with: alarmForm.shared_with });
    if (res.success) {
      await loadCalendarData();
      setShowAddAlarm(false);
      setAlarmForm({ title:"", date:"", time:"", note:"", sound:"default", repeat:"none", shared_with:[] });
      showToast("یادآور ثبت شد");
    }
  };

  // Calendar helpers
  const daysInMonth    = getJalaliDaysInMonth(calYear, calMonth);
  const firstDay       = getFirstDayOfWeek(calYear, calMonth);
  const selectedDateStr = selectedDay ? `${calYear}/${String(calMonth).padStart(2,"0")}/${String(selectedDay).padStart(2,"0")}` : "";
  const dayEvents = events.filter(e => e.date === selectedDateStr);
  const dayAlarms = alarms.filter(a => a.date === selectedDateStr);
  const prevMonth = () => { if (calMonth===1){setCalYear(y=>y-1);setCalMonth(12);}else setCalMonth(m=>m-1); };
  const nextMonth = () => { if (calMonth===12){setCalYear(y=>y+1);setCalMonth(1);}else setCalMonth(m=>m+1); };

  const filteredRooms = rooms.filter(r =>
    r.name?.includes(search) || r.members?.some(m => m.name?.includes(search))
  );

  const fmtTime = (iso:string) => {
    try { return new Date(iso).toLocaleTimeString("fa-IR",{hour:"2-digit",minute:"2-digit"}); } catch { return ""; }
  };

  const inp = (val:string, onChange:(v:string)=>void, ph?:string, multiline?:boolean) => {
    const st: React.CSSProperties = { width:"100%", padding:"9px 12px", borderRadius:10, border:`1.5px solid ${border}`, background:dark?"rgba(255,255,255,0.05)":"#f8fafc", color:T.text, fontSize:13, fontFamily:"inherit", outline:"none", boxSizing:"border-box", resize:"vertical" as const };
    if (multiline) return <textarea value={val} onChange={e=>onChange(e.target.value)} placeholder={ph} rows={2} style={st}/>;
    return <input value={val} onChange={e=>onChange(e.target.value)} placeholder={ph} style={st}/>;
  };

  const Label = ({ children }: { children: React.ReactNode }) => (
    <div style={{ fontSize:11, fontWeight:700, color:T.subtext, marginBottom:4 }}>{children}</div>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ height:"calc(100vh - 76px)", display:"flex", flexDirection:"column", fontFamily:"'Vazirmatn','Segoe UI',sans-serif", background:bg, borderRadius:16, overflow:"hidden", border:`1px solid ${border}` }}>

      {/* Active Alarm Popup */}
      {activeAlarm && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.75)", zIndex:99999, display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div style={{ background:panel, borderRadius:24, padding:36, maxWidth:400, width:"90%", textAlign:"center", boxShadow:"0 24px 64px rgba(0,0,0,0.5)", animation:"pulse 1s infinite" }}>
            <div style={{ fontSize:56, marginBottom:16 }}>⏰</div>
            <h2 style={{ color:T.text, margin:"0 0 8px", fontSize:22, fontWeight:800 }}>{activeAlarm.title}</h2>
            {activeAlarm.note && <p style={{ color:T.subtext, fontSize:13, margin:"0 0 24px" }}>{activeAlarm.note}</p>}
            <div style={{ display:"flex", gap:12, justifyContent:"center" }}>
              <button onClick={()=>{setActiveAlarm(null); chatAPI.updateAlarm?.({ id:activeAlarm.id, done:1 });}} style={{ padding:"11px 28px", borderRadius:12, border:"none", background:accent, color:"#fff", fontWeight:700, cursor:"pointer", fontSize:14, fontFamily:"inherit" }}>بستن</button>
              <button onClick={()=>{ setActiveAlarm(null); showToast("۵ دقیقه به تعویق افتاد"); }} style={{ padding:"11px 28px", borderRadius:12, border:`1px solid ${border}`, background:"transparent", color:T.text, fontWeight:700, cursor:"pointer", fontSize:14, fontFamily:"inherit" }}>۵ دقیقه دیگر</button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast.text && (
        <div style={{ position:"fixed", top:20, left:"50%", transform:"translateX(-50%)", background:toast.ok?"#10b981":"#ef4444", color:"#fff", padding:"12px 28px", borderRadius:12, fontSize:13, fontWeight:700, zIndex:9998, boxShadow:"0 4px 20px rgba(0,0,0,0.25)" }}>
          {toast.text}
        </div>
      )}

      {/* Profile Modal */}
      {showProfile && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.65)", zIndex:9997, display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div style={{ background:panel, borderRadius:20, padding:28, width:380, maxWidth:"92vw", boxShadow:"0 20px 60px rgba(0,0,0,0.4)" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
              <h3 style={{ margin:0, color:T.text, fontSize:16, fontWeight:800 }}>پروفایل من</h3>
              <button onClick={()=>setShowProfile(false)} style={{ background:"none", border:"none", cursor:"pointer", color:T.subtext }}><X size={18}/></button>
            </div>
            <div style={{ textAlign:"center", marginBottom:20 }}>
              <div style={{ position:"relative", display:"inline-block" }}>
                <Avatar name={profileForm.full_name||"من"} avatar={profileForm.avatar} size={88}/>
                <button onClick={()=>avatarInputRef.current?.click()} style={{ position:"absolute", bottom:0, right:0, width:30, height:30, borderRadius:"50%", background:accent, border:"2px solid white", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>
                  <Camera size={13} color="#fff"/>
                </button>
                <input ref={avatarInputRef} type="file" accept="image/*" style={{ display:"none" }} onChange={async e=>{
                  const file = e.target.files?.[0]; if (!file) return;
                  setProfileUploading(true);
                  const r = await chatAPI.uploadFile(file);
                  if (r.success) setProfileForm(p=>({...p, avatar:r.url}));
                  setProfileUploading(false);
                }}/>
              </div>
              {profileUploading && <div style={{ fontSize:11, color:T.subtext, marginTop:6 }}>در حال آپلود...</div>}
            </div>
            <div style={{ marginBottom:14 }}>
              <Label>نام کامل</Label>
              {inp(profileForm.full_name, v=>setProfileForm(p=>({...p, full_name:v})), "نام کامل")}
            </div>
            <button onClick={async()=>{
              const r = await chatAPI.updateProfile(profileForm);
              if (r.success) { setMyProfile((p:any)=>({...p,...profileForm})); showToast("پروفایل ذخیره شد"); setShowProfile(false); }
              else showToast("خطا", false);
            }} style={{ width:"100%", padding:"11px", borderRadius:10, border:"none", background:accent, color:"#fff", fontWeight:700, cursor:"pointer", fontSize:13, fontFamily:"inherit" }}>
              ذخیره تغییرات
            </button>
          </div>
        </div>
      )}

      {/* New Room Modal */}
      {showNewRoom && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.65)", zIndex:9997, display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div style={{ background:panel, borderRadius:20, padding:28, width:440, maxWidth:"92vw", maxHeight:"85vh", overflowY:"auto", boxShadow:"0 20px 60px rgba(0,0,0,0.4)" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
              <h3 style={{ margin:0, color:T.text, fontSize:16, fontWeight:800 }}>گفتگوی جدید</h3>
              <button onClick={()=>setShowNewRoom(false)} style={{ background:"none", border:"none", cursor:"pointer", color:T.subtext }}><X size={18}/></button>
            </div>

            {/* Type */}
            <div style={{ display:"flex", gap:8, marginBottom:16 }}>
              {[{v:"direct",l:"پیام مستقیم"},{v:"group",l:"گروه جدید"}].map(t=>(
                <button key={t.v} onClick={()=>setNewRoomForm(f=>({...f,type:t.v as any,members:[],name:""}))} style={{ flex:1, padding:"9px", borderRadius:10, border:`2px solid ${newRoomForm.type===t.v?accent:border}`, background:newRoomForm.type===t.v?`${accent}18`:"transparent", color:newRoomForm.type===t.v?accent:T.text, fontWeight:700, cursor:"pointer", fontSize:13, fontFamily:"inherit" }}>
                  {t.l}
                </button>
              ))}
            </div>

            {/* Group name */}
            {newRoomForm.type === "group" && (
              <div style={{ marginBottom:14 }}>
                <Label>نام گروه *</Label>
                {inp(newRoomForm.name, v=>setNewRoomForm(f=>({...f,name:v})), "نام گروه را وارد کنید")}
              </div>
            )}

            {/* Members */}
            <div style={{ marginBottom:16 }}>
              <Label>{newRoomForm.type==="direct" ? "انتخاب کاربر *" : "اضافه کردن اعضا"}</Label>
              <div style={{ display:"flex", flexDirection:"column", gap:6, maxHeight:200, overflowY:"auto" }}>
                {users.filter(u=>u.id!==myId).map(u=>{
                  const sel = newRoomForm.members.includes(u.id);
                  return (
                    <div key={u.id} onClick={()=>{
                      if (newRoomForm.type==="direct") {
                        setNewRoomForm(f=>({...f, members:[u.id]}));
                      } else {
                        setNewRoomForm(f=>({...f, members: sel ? f.members.filter(id=>id!==u.id) : [...f.members,u.id]}));
                      }
                    }} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 12px", borderRadius:10, cursor:"pointer", background:sel?`${accent}18`:"transparent", border:`1px solid ${sel?accent:border}`, transition:"all .15s" }}>
                      <Avatar name={u.name} avatar={u.avatar} size={34} online={u.is_online}/>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:13, fontWeight:700, color:T.text }}>{u.name}</div>
                        <div style={{ fontSize:10, color:T.subtext }}>{u.is_online?"آنلاین":"آفلاین"}</div>
                      </div>
                      {sel && <Check size={16} color={accent}/>}
                    </div>
                  );
                })}
              </div>
            </div>

            <button onClick={createRoom} style={{ width:"100%", padding:"12px", borderRadius:10, border:"none", background:accent, color:"#fff", fontWeight:700, cursor:"pointer", fontSize:14, fontFamily:"inherit" }}>
              {newRoomForm.type==="direct" ? "شروع گفتگو" : "ساخت گروه"}
            </button>
          </div>
        </div>
      )}

      {/* Room Info Modal */}
      {showRoomInfo && activeRoom && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.65)", zIndex:9997, display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div style={{ background:panel, borderRadius:20, padding:28, width:400, maxWidth:"92vw", maxHeight:"85vh", overflowY:"auto", boxShadow:"0 20px 60px rgba(0,0,0,0.4)" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
              <h3 style={{ margin:0, color:T.text, fontSize:16, fontWeight:800 }}>{activeRoom.type==="group"?"اطلاعات گروه":"اطلاعات گفتگو"}</h3>
              <button onClick={()=>setShowRoomInfo(false)} style={{ background:"none", border:"none", cursor:"pointer", color:T.subtext }}><X size={18}/></button>
            </div>

            <div style={{ textAlign:"center", marginBottom:20 }}>
              {activeRoom.type==="group"
                ? <div style={{ width:72, height:72, borderRadius:"50%", background:`${accent}22`, display:"flex", alignItems:"center", justifyContent:"center", color:accent, margin:"0 auto 8px" }}><Hash size={28}/></div>
                : <div style={{ margin:"0 auto 8px", width:72 }}><Avatar name={activeRoom.name} avatar={activeRoom.avatar} size={72}/></div>
              }
              <div style={{ fontSize:16, fontWeight:800, color:T.text }}>{activeRoom.name}</div>
              {activeRoom.description && <div style={{ fontSize:12, color:T.subtext, marginTop:4 }}>{activeRoom.description}</div>}
            </div>

            {/* Members */}
            <div style={{ marginBottom:16 }}>
              <div style={{ fontSize:12, fontWeight:700, color:T.subtext, marginBottom:8 }}>اعضا ({activeRoom.members?.length||0})</div>
              {activeRoom.members?.map(m=>(
                <div key={m.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 0", borderBottom:`1px solid ${border}` }}>
                  <Avatar name={m.name} avatar={m.avatar} size={36} online={m.is_online}/>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13, fontWeight:600, color:T.text }}>{m.name}</div>
                    <div style={{ fontSize:10, color:T.subtext }}>{m.is_online?"آنلاین":"آفلاین"}{m.room_role==="admin"?" · ادمین":""}</div>
                  </div>
                  {activeRoom.created_by===myId && m.id!==myId && (
                    <button onClick={async()=>{
                      await chatAPI.removeMember(activeRoom.id, m.id);
                      const r = await chatAPI.getRooms();
                      if (r.success) { setRooms(r.rooms||[]); const updated = (r.rooms||[]).find((rm:Room)=>rm.id===activeRoom.id); if (updated) setActiveRoom(updated); }
                    }} style={{ background:"none", border:"none", cursor:"pointer", color:"#ef4444", padding:4 }}><Trash2 size={13}/></button>
                  )}
                </div>
              ))}
            </div>

            {/* Add member */}
            {activeRoom.type==="group" && activeRoom.created_by===myId && (
              <div style={{ marginBottom:16 }}>
                <div style={{ fontSize:12, fontWeight:700, color:T.subtext, marginBottom:8 }}>افزودن عضو</div>
                <div style={{ display:"flex", flexDirection:"column", gap:4, maxHeight:120, overflowY:"auto" }}>
                  {users.filter(u=>u.id!==myId && !activeRoom.members?.some(m=>m.id===u.id)).map(u=>(
                    <div key={u.id} onClick={async()=>{
                      await chatAPI.addMember(activeRoom.id, u.id);
                      const r = await chatAPI.getRooms();
                      if (r.success) { setRooms(r.rooms||[]); const updated=(r.rooms||[]).find((rm:Room)=>rm.id===activeRoom.id); if(updated) setActiveRoom(updated); }
                      showToast(`${u.name} اضافه شد`);
                    }} style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 10px", borderRadius:8, cursor:"pointer", background:"transparent", border:`1px solid ${border}` }}>
                      <Avatar name={u.name} avatar={u.avatar} size={28}/>
                      <span style={{ fontSize:12, color:T.text }}>{u.name}</span>
                      <UserPlus size={13} color={accent} style={{ marginRight:"auto" }}/>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Leave/Delete */}
            <div style={{ display:"flex", gap:8 }}>
              {activeRoom.created_by===myId ? (
                <button onClick={async()=>{
                  if (!window.confirm("آیا مطمئنید؟")) return;
                  await chatAPI.deleteRoom(activeRoom.id);
                  setActiveRoom(null); setShowRoomInfo(false); loadRooms();
                  showToast("گفتگو حذف شد");
                }} style={{ flex:1, padding:"10px", borderRadius:10, border:"none", background:"#ef444418", color:"#ef4444", fontWeight:700, cursor:"pointer", fontSize:13, fontFamily:"inherit" }}>
                  <Trash2 size={14} style={{ marginLeft:4 }}/> حذف گروه
                </button>
              ) : (
                <button onClick={async()=>{
                  await chatAPI.removeMember(activeRoom.id, myId);
                  setActiveRoom(null); setShowRoomInfo(false); loadRooms();
                  showToast("از گروه خارج شدید");
                }} style={{ flex:1, padding:"10px", borderRadius:10, border:"none", background:"#f59e0b18", color:"#f59e0b", fontWeight:700, cursor:"pointer", fontSize:13, fontFamily:"inherit" }}>
                  <LogOut size={14} style={{ marginLeft:4 }}/> خروج از گروه
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Top Tab Bar */}
      <div style={{ display:"flex", alignItems:"center", background:panel, borderBottom:`1px solid ${border}`, padding:"0 16px", flexShrink:0 }}>
        {[{id:"chat",icon:<MessageCircle size={15}/>,label:"گفتگو"},{id:"calendar",icon:<Calendar size={15}/>,label:"تقویم"}].map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id as any)} style={{ display:"flex", alignItems:"center", gap:6, padding:"14px 18px", borderBottom:tab===t.id?`3px solid ${accent}`:"3px solid transparent", borderTop:"none", borderLeft:"none", borderRight:"none", background:"none", color:tab===t.id?accent:T.subtext, fontWeight:700, fontSize:13, cursor:"pointer", fontFamily:"inherit" }}>
            {t.icon}{t.label}
          </button>
        ))}
        <div style={{ flex:1 }}/>
        <span style={{ fontSize:11, color:T.subtext, marginLeft:12 }}>
          {users.filter(u=>u.is_online).length} آنلاین
        </span>
        <button onClick={()=>setShowProfile(true)} style={{ background:"none", border:"none", cursor:"pointer", padding:"4px 8px", borderRadius:8, display:"flex", alignItems:"center" }}>
          <Avatar name={myProfile?.full_name||"من"} avatar={myProfile?.avatar} size={32}/>
        </button>
      </div>

      {/* ════ CHAT TAB ════ */}
      {tab === "chat" && (
        <div style={{ flex:1, display:"flex", overflow:"hidden" }}>

          {/* Sidebar */}
          <div style={{ width:"clamp(220px,28%,300px)", background:panel, borderLeft:`1px solid ${border}`, display:"flex", flexDirection:"column", flexShrink:0 }}>
            {/* Search + New */}
            <div style={{ padding:"12px 12px", borderBottom:`1px solid ${border}`, display:"flex", gap:8 }}>
              <div style={{ flex:1, position:"relative" }}>
                <Search size={13} style={{ position:"absolute", right:10, top:"50%", transform:"translateY(-50%)", color:T.subtext, pointerEvents:"none" }}/>
                <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="جستجو..." style={{ width:"100%", padding:"8px 30px 8px 10px", borderRadius:10, border:`1px solid ${border}`, background:dark?"#0f172a":"#f1f5f9", color:T.text, fontSize:12, fontFamily:"inherit", outline:"none", boxSizing:"border-box" }}/>
              </div>
              <button onClick={()=>setShowNewRoom(true)} style={{ padding:"8px 10px", borderRadius:10, border:"none", background:accent, color:"#fff", cursor:"pointer", display:"flex", alignItems:"center" }}>
                <Plus size={15}/>
              </button>
            </div>

            {/* Room List */}
            <div style={{ flex:1, overflowY:"auto" }}>
              {filteredRooms.length === 0 && (
                <div style={{ padding:24, textAlign:"center", color:T.subtext, fontSize:12 }}>
                  گفتگویی ندارید<br/>
                  <span style={{ fontSize:11 }}>روی + کلیک کنید</span>
                </div>
              )}
              {filteredRooms.map(room => {
                const isGroup = room.type === "group";
                const other   = !isGroup ? room.members?.find(m=>m.id!==myId) : null;
                return (
                  <div key={room.id} onClick={()=>openRoom(room)} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 12px", cursor:"pointer", background:activeRoom?.id===room.id?(dark?"#1e3a5f":"#eff6ff"):"transparent", borderBottom:`1px solid ${border}`, transition:"background .15s" }}>
                    {isGroup
                      ? <div style={{ width:42, height:42, borderRadius:"50%", background:`${accent}22`, display:"flex", alignItems:"center", justifyContent:"center", color:accent, flexShrink:0 }}><Hash size={18}/></div>
                      : <Avatar name={other?.name||room.name} avatar={other?.avatar} size={42} online={other?.is_online}/>
                    }
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                        <span style={{ fontWeight:700, fontSize:13, color:T.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{isGroup?room.name:(other?.name||room.name)}</span>
                        <span style={{ fontSize:10, color:T.subtext, flexShrink:0, marginRight:4 }}>{room.last_message?fmtTime(room.last_message.created_at):""}</span>
                      </div>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:2 }}>
                        <span style={{ fontSize:11, color:T.subtext, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", flex:1 }}>
                          {room.last_message?.type==="voice"?"🎤 صوتی":room.last_message?.type==="file"?"📎 فایل":room.last_message?.type==="image"?"🖼 عکس":room.last_message?.content||"هنوز پیامی نیست"}
                        </span>
                        {room.unread_count > 0 && <span style={{ background:accent, color:"#fff", borderRadius:10, padding:"1px 7px", fontSize:10, fontWeight:700, flexShrink:0, marginRight:4 }}>{room.unread_count}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Online users */}
            <div style={{ borderTop:`1px solid ${border}`, padding:"10px 12px" }}>
              <div style={{ fontSize:11, fontWeight:700, color:T.subtext, marginBottom:6 }}>آنلاین</div>
              <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
                {users.filter(u=>u.is_online && u.id!==myId).slice(0,8).map(u=>(
                  <div key={u.id} title={u.name} onClick={async()=>{
                    const res = await chatAPI.createRoom({ name:"", type:"direct", members:[u.id] });
                    if (res.success) { await loadRooms(); const r2=await chatAPI.getRooms(); if(r2.success){const rm=(r2.rooms||[]).find((x:Room)=>x.id===res.room_id);if(rm)openRoom(rm);} }
                  }} style={{ cursor:"pointer" }}>
                    <Avatar name={u.name} avatar={u.avatar} size={30} online={true}/>
                  </div>
                ))}
                {users.filter(u=>u.is_online).length===0 && <span style={{ fontSize:11, color:T.subtext }}>هیچ‌کس آنلاین نیست</span>}
              </div>
            </div>
          </div>

          {/* Chat Area */}
          {activeRoom ? (
            <div style={{ flex:1, display:"flex", flexDirection:"column", background:panel2, minWidth:0 }}>
              {/* Header */}
              <div style={{ display:"flex", alignItems:"center", gap:12, padding:"11px 16px", borderBottom:`1px solid ${border}`, background:panel, flexShrink:0 }}>
                {activeRoom.type==="group"
                  ? <div style={{ width:40, height:40, borderRadius:"50%", background:`${accent}22`, display:"flex", alignItems:"center", justifyContent:"center", color:accent }}><Hash size={18}/></div>
                  : <Avatar name={activeRoom.members?.find(m=>m.id!==myId)?.name||activeRoom.name} avatar={activeRoom.members?.find(m=>m.id!==myId)?.avatar} size={40} online={activeRoom.members?.find(m=>m.id!==myId)?.is_online}/>
                }
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:700, fontSize:14, color:T.text }}>{activeRoom.name}</div>
                  <div style={{ fontSize:11, color:T.subtext }}>
                    {activeRoom.type==="group"
                      ? `${activeRoom.members?.length||0} عضو`
                      : (activeRoom.members?.find(m=>m.id!==myId)?.is_online?"🟢 آنلاین":"⚫ آفلاین")
                    }
                  </div>
                </div>
                <button onClick={()=>setShowRoomInfo(true)} style={{ background:"none", border:"none", cursor:"pointer", color:T.subtext, padding:6, borderRadius:8 }}><MoreVertical size={18}/></button>
              </div>

              {/* Messages */}
              <div style={{ flex:1, overflowY:"auto", padding:"14px 16px", display:"flex", flexDirection:"column", gap:6 }}>
                {msgLoading && <div style={{ textAlign:"center", color:T.subtext, padding:20 }}>در حال بارگذاری...</div>}
                {messages.map(msg => {
                  const isMine = msg.sender_id === myId;
                  return (
                    <div key={msg.id} style={{ display:"flex", justifyContent:isMine?"flex-start":"flex-end", gap:8, alignItems:"flex-end" }}>
                      {!isMine && <Avatar name={msg.sender_name} avatar={msg.sender_avatar} size={28}/>}
                      <div style={{ maxWidth:"68%", position:"relative" }} onDoubleClick={()=>setReplyTo(msg)}>
                        {!isMine && activeRoom.type==="group" && (
                          <div style={{ fontSize:10, color:accent, fontWeight:700, marginBottom:2, textAlign:"right" }}>{msg.sender_name}</div>
                        )}
                        {msg.reply_to && (
                          <div style={{ background:dark?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.06)", borderRight:`3px solid ${accent}`, padding:"3px 8px", borderRadius:6, fontSize:10, color:T.subtext, marginBottom:4 }}>پاسخ به پیام</div>
                        )}
                        <div style={{ background:isMine?accent:(dark?"#1e293b":"#ffffff"), color:isMine?"#fff":T.text, padding:"8px 12px", borderRadius:isMine?"14px 14px 4px 14px":"14px 14px 14px 4px", fontSize:13, boxShadow:"0 1px 3px rgba(0,0,0,0.1)", wordBreak:"break-word", border:!isMine?`1px solid ${border}`:"none" }}>
                          {msg.type==="voice" ? (
                            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                              <button onClick={()=>{
                                if (!audioRefs.current[msg.id]) audioRefs.current[msg.id] = new Audio(`https://www.hamidigroupfinance.com${msg.file_url}`);
                                const audio = audioRefs.current[msg.id];
                                if (playingVoice===msg.id) { audio.pause(); setPlayingVoice(null); }
                                else { audio.play(); setPlayingVoice(msg.id); audio.onended=()=>setPlayingVoice(null); }
                              }} style={{ background:"none", border:"none", cursor:"pointer", color:isMine?"#fff":accent, padding:0 }}>
                                {playingVoice===msg.id ? <Pause size={18}/> : <Play size={18}/>}
                              </button>
                              <span style={{ fontSize:11 }}>🎤 {msg.duration||0}ث</span>
                            </div>
                          ) : msg.type==="image" && msg.file_url ? (
                            <img src={`https://www.hamidigroupfinance.com${msg.file_url}`} alt="عکس" style={{ maxWidth:220, maxHeight:200, borderRadius:8, display:"block" }}/>
                          ) : msg.type==="file" && msg.file_url ? (
                            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                              <Paperclip size={14}/>
                              <span style={{ fontSize:12 }}>{msg.file_name||"فایل"}</span>
                              <a href={`https://www.hamidigroupfinance.com${msg.file_url}`} download style={{ color:isMine?"#fff":accent }}><Download size={13}/></a>
                            </div>
                          ) : (
                            <span>{msg.content}</span>
                          )}
                        </div>
                        <div style={{ fontSize:10, color:T.subtext, textAlign:isMine?"right":"left", marginTop:2, display:"flex", alignItems:"center", gap:4, justifyContent:isMine?"flex-end":"flex-start" }}>
                          {fmtTime(msg.created_at)}
                          {isMine && (msg.is_read ? <CheckCheck size={12} color="#10b981"/> : <Check size={12}/>)}
                        </div>
                      </div>
                      {isMine && <Avatar name={myProfile?.full_name||"من"} avatar={myProfile?.avatar} size={28}/>}
                    </div>
                  );
                })}
                <div ref={messagesEndRef}/>
              </div>

              {/* Reply indicator */}
              {replyTo && (
                <div style={{ padding:"6px 16px", background:dark?"rgba(99,102,241,0.1)":"rgba(99,102,241,0.06)", borderTop:`1px solid ${border}`, display:"flex", alignItems:"center", gap:8 }}>
                  <div style={{ flex:1, fontSize:11, color:T.subtext, borderRight:`3px solid ${accent}`, paddingRight:8 }}>
                    پاسخ به: {replyTo.content.substring(0,40)}
                  </div>
                  <button onClick={()=>setReplyTo(null)} style={{ background:"none", border:"none", cursor:"pointer", color:T.subtext }}><X size={14}/></button>
                </div>
              )}

              {/* Input */}
              <div style={{ padding:"10px 14px", borderTop:`1px solid ${border}`, background:panel, display:"flex", gap:8, alignItems:"center", flexShrink:0 }}>
                <button onClick={()=>fileInputRef.current?.click()} style={{ background:"none", border:"none", cursor:"pointer", color:T.subtext, padding:6, borderRadius:8, flexShrink:0 }}><Paperclip size={18}/></button>
                <input ref={fileInputRef} type="file" style={{ display:"none" }} onChange={e=>{const f=e.target.files?.[0];if(f)sendFile(f);e.target.value="";}}/>
                <input value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&!e.shiftKey&&sendMessage()} placeholder="پیام بنویسید..." style={{ flex:1, padding:"10px 14px", borderRadius:22, border:`1.5px solid ${border}`, background:dark?"rgba(255,255,255,0.05)":"#f1f5f9", color:T.text, fontSize:13, fontFamily:"inherit", outline:"none" }}/>
                {recording ? (
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <span style={{ fontSize:12, color:"#ef4444", fontWeight:700 }}>🔴 {recordSeconds}ث</span>
                    <button onClick={stopRecording} style={{ padding:"8px 14px", borderRadius:22, border:"none", background:"#ef4444", color:"#fff", cursor:"pointer", fontFamily:"inherit", fontWeight:700, fontSize:12 }}>ارسال</button>
                  </div>
                ) : (
                  <>
                    <button onClick={startRecording} style={{ background:"none", border:"none", cursor:"pointer", color:T.subtext, padding:6, borderRadius:8, flexShrink:0 }}><Mic size={18}/></button>
                    <button onClick={sendMessage} disabled={!input.trim()} style={{ padding:"8px 16px", borderRadius:22, border:"none", background:input.trim()?accent:"#94a3b8", color:"#fff", cursor:input.trim()?"pointer":"default", display:"flex", alignItems:"center", gap:4, flexShrink:0, fontFamily:"inherit", fontWeight:600, fontSize:13 }}>
                      <Send size={14}/> ارسال
                    </button>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column", gap:12, color:T.subtext }}>
              <MessageCircle size={48} color={T.subtext}/>
              <div style={{ fontSize:14, fontWeight:600 }}>یک گفتگو انتخاب کنید</div>
              <button onClick={()=>setShowNewRoom(true)} style={{ padding:"10px 24px", borderRadius:12, border:"none", background:accent, color:"#fff", cursor:"pointer", fontSize:13, fontWeight:700, fontFamily:"inherit" }}>
                <Plus size={14} style={{ marginLeft:4 }}/> گفتگوی جدید
              </button>
            </div>
          )}
        </div>
      )}

      {/* ════ CALENDAR TAB ════ */}
      {tab === "calendar" && (
        <div style={{ flex:1, overflowY:"auto", padding:20, display:"flex", gap:16, flexWrap:"wrap", alignContent:"flex-start" }}>

          {/* Calendar grid */}
          <div style={{ flex:"1 1 320px", background:panel, borderRadius:16, padding:20, border:`1px solid ${border}` }}>
            {/* Header */}
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
              <button onClick={prevMonth} style={{ background:"none", border:"none", cursor:"pointer", color:T.text, padding:6, borderRadius:8 }}><ChevronRight size={18}/></button>
              <div style={{ fontSize:15, fontWeight:800, color:T.text }}>
                {JALALI_MONTHS[calMonth-1]} {calYear}
              </div>
              <button onClick={nextMonth} style={{ background:"none", border:"none", cursor:"pointer", color:T.text, padding:6, borderRadius:8 }}><ChevronLeft size={18}/></button>
            </div>

            {/* Day headers */}
            <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", marginBottom:8 }}>
              {JALALI_DAYS.map(d=>(
                <div key={d} style={{ textAlign:"center", fontSize:11, fontWeight:700, color:accent, padding:"4px 0" }}>{d}</div>
              ))}
            </div>

            {/* Days */}
            <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:3 }}>
              {Array.from({length:firstDay}).map((_,i)=><div key={"e"+i}/>)}
              {Array.from({length:daysInMonth}).map((_,i)=>{
                const d = i+1;
                const dateStr = `${calYear}/${String(calMonth).padStart(2,"0")}/${String(d).padStart(2,"0")}`;
                const hasEvent = events.some(e=>e.date===dateStr);
                const hasAlarm = alarms.some(a=>a.date===dateStr);
                const isSel    = selectedDay===d;
                const isToday  = jToday.jy===calYear && jToday.jm===calMonth && jToday.jd===d;
                return (
                  <button key={d} onClick={()=>setSelectedDay(d)} style={{ padding:"8px 0", border:"none", borderRadius:10, cursor:"pointer", fontSize:12, fontFamily:"inherit", background:isSel?accent:isToday?`${accent}22`:"transparent", color:isSel?"#fff":isToday?accent:T.text, fontWeight:isSel||isToday?700:400, position:"relative" }}>
                    {d}
                    {(hasEvent||hasAlarm) && <span style={{ position:"absolute", bottom:3, left:"50%", transform:"translateX(-50%)", width:4, height:4, borderRadius:"50%", background:isSel?"#fff":hasAlarm?"#ef4444":"#10b981" }}/>}
                  </button>
                );
              })}
            </div>

            {/* Buttons */}
            <div style={{ display:"flex", gap:8, marginTop:16 }}>
              <button onClick={()=>setShowAddEvent(true)} style={{ flex:1, padding:"9px", borderRadius:10, border:`1px solid ${border}`, background:"transparent", color:accent, fontWeight:700, cursor:"pointer", fontSize:12, fontFamily:"inherit" }}>
                + رویداد
              </button>
              <button onClick={()=>setShowAddAlarm(true)} style={{ flex:1, padding:"9px", borderRadius:10, border:`1px solid ${border}`, background:"transparent", color:"#ef4444", fontWeight:700, cursor:"pointer", fontSize:12, fontFamily:"inherit" }}>
                + یادآور
              </button>
            </div>
          </div>

          {/* Day events & alarms */}
          <div style={{ flex:"1 1 280px", display:"flex", flexDirection:"column", gap:12 }}>
            {selectedDay && (
              <div style={{ background:panel, borderRadius:16, padding:20, border:`1px solid ${border}` }}>
                <div style={{ fontSize:14, fontWeight:800, color:T.text, marginBottom:12 }}>
                  {selectedDay} {JALALI_MONTHS[calMonth-1]} {calYear}
                </div>

                {dayEvents.length === 0 && dayAlarms.length === 0 && (
                  <div style={{ fontSize:12, color:T.subtext, textAlign:"center", padding:"20px 0" }}>رویداد یا یادآوری ندارید</div>
                )}

                {dayEvents.map(ev=>(
                  <div key={ev.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 12px", borderRadius:12, background:dark?`${ev.color}18`:`${ev.color}10`, border:`1px solid ${ev.color}40`, marginBottom:8 }}>
                    <div style={{ width:10, height:10, borderRadius:"50%", background:ev.color, flexShrink:0 }}/>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:700, color:T.text, textDecoration:ev.done?"line-through":"none" }}>{ev.title}</div>
                      {ev.time && <div style={{ fontSize:11, color:T.subtext }}>{ev.time}</div>}
                      {ev.note && <div style={{ fontSize:11, color:T.subtext }}>{ev.note}</div>}
                    </div>
                    <div style={{ display:"flex", gap:4 }}>
                      {!ev.done && <button onClick={async()=>{ await chatAPI.updateEvent({id:ev.id,done:1}); loadCalendarData(); }} style={{ background:"none", border:"none", cursor:"pointer", color:"#10b981" }}><Check size={14}/></button>}
                      <button onClick={async()=>{ await chatAPI.deleteEvent(ev.id); loadCalendarData(); }} style={{ background:"none", border:"none", cursor:"pointer", color:"#ef4444" }}><Trash2 size={13}/></button>
                    </div>
                  </div>
                ))}

                {dayAlarms.map(al=>(
                  <div key={al.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 12px", borderRadius:12, background:dark?"rgba(239,68,68,0.1)":"#fff5f5", border:"1px solid #fecaca", marginBottom:8 }}>
                    <Bell size={14} color="#ef4444"/>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:700, color:T.text }}>{al.title}</div>
                      <div style={{ fontSize:11, color:"#ef4444" }}>{al.time}</div>
                      {al.note && <div style={{ fontSize:11, color:T.subtext }}>{al.note}</div>}
                    </div>
                    <button onClick={async()=>{ await chatAPI.deleteAlarm(al.id); loadCalendarData(); }} style={{ background:"none", border:"none", cursor:"pointer", color:"#ef4444" }}><Trash2 size={13}/></button>
                  </div>
                ))}
              </div>
            )}

            {/* All alarms */}
            {alarms.length > 0 && (
              <div style={{ background:panel, borderRadius:16, padding:20, border:`1px solid ${border}` }}>
                <div style={{ fontSize:13, fontWeight:800, color:T.text, marginBottom:10 }}>همه یادآورها ({alarms.length})</div>
                {alarms.map(al=>(
                  <div key={al.id} style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 0", borderBottom:`1px solid ${border}` }}>
                    <Clock size={13} color="#ef4444"/>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:12, fontWeight:600, color:T.text }}>{al.title}</div>
                      <div style={{ fontSize:10, color:T.subtext }}>{al.date} — {al.time}</div>
                    </div>
                    <button onClick={async()=>{ await chatAPI.deleteAlarm(al.id); loadCalendarData(); }} style={{ background:"none", border:"none", cursor:"pointer", color:"#ef4444" }}><Trash2 size={12}/></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add Event Modal */}
      {showAddEvent && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.65)", zIndex:9997, display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div style={{ background:panel, borderRadius:20, padding:28, width:400, maxWidth:"92vw", boxShadow:"0 20px 60px rgba(0,0,0,0.4)" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
              <h3 style={{ margin:0, color:T.text, fontSize:16, fontWeight:800 }}>رویداد جدید</h3>
              <button onClick={()=>setShowAddEvent(false)} style={{ background:"none", border:"none", cursor:"pointer", color:T.subtext }}><X size={18}/></button>
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
              <div><Label>عنوان *</Label>{inp(eventForm.title, v=>setEventForm(f=>({...f,title:v})), "عنوان رویداد")}</div>
              <div><Label>نوع</Label>
                <div style={{ display:"flex", gap:6 }}>
                  {[{v:"event",l:"رویداد"},{v:"task",l:"وظیفه"},{v:"reminder",l:"یادآور"}].map(t=>(
                    <button key={t.v} onClick={()=>setEventForm(f=>({...f,type:t.v as any}))} style={{ flex:1, padding:"8px", borderRadius:9, border:`1.5px solid ${eventForm.type===t.v?accent:border}`, background:eventForm.type===t.v?`${accent}18`:"transparent", color:eventForm.type===t.v?accent:T.text, fontWeight:600, cursor:"pointer", fontSize:12, fontFamily:"inherit" }}>{t.l}</button>
                  ))}
                </div>
              </div>
              <div><Label>ساعت</Label><input value={eventForm.time} onChange={e=>setEventForm(f=>({...f,time:e.target.value}))} type="time" style={{ width:"100%", padding:"9px 12px", borderRadius:10, border:`1.5px solid ${border}`, background:dark?"rgba(255,255,255,0.05)":"#f8fafc", color:T.text, fontSize:13, fontFamily:"inherit", outline:"none", boxSizing:"border-box" as const }}/></div>
              <div>
                <Label>رنگ</Label>
                <div style={{ display:"flex", gap:6 }}>
                  {["#3b82f6","#10b981","#f59e0b","#ef4444","#8b5cf6","#06b6d4"].map(c=>(
                    <button key={c} onClick={()=>setEventForm(f=>({...f,color:c}))} style={{ width:28, height:28, borderRadius:"50%", border:eventForm.color===c?"3px solid #fff":"3px solid transparent", background:c, cursor:"pointer", outline:eventForm.color===c?`2px solid ${c}`:"none" }}/>
                  ))}
                </div>
              </div>
              <div><Label>توضیحات</Label>{inp(eventForm.note, v=>setEventForm(f=>({...f,note:v})), "توضیحات اختیاری", true)}</div>
              <div>
                <Label>اشتراک با</Label>
                <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                  {users.filter(u=>u.id!==myId).map(u=>{
                    const sel = eventForm.shared_with.includes(u.id);
                    return <button key={u.id} onClick={()=>setEventForm(f=>({...f, shared_with: sel?f.shared_with.filter(id=>id!==u.id):[...f.shared_with,u.id]}))} style={{ padding:"4px 10px", borderRadius:8, border:`1px solid ${sel?accent:border}`, background:sel?`${accent}18`:"transparent", color:sel?accent:T.text, fontSize:11, cursor:"pointer", fontFamily:"inherit" }}>{u.name}</button>;
                  })}
                </div>
              </div>
              <button onClick={saveEvent} style={{ padding:"11px", borderRadius:10, border:"none", background:accent, color:"#fff", fontWeight:700, cursor:"pointer", fontSize:13, fontFamily:"inherit" }}>ثبت رویداد</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Alarm Modal */}
      {showAddAlarm && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.65)", zIndex:9997, display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div style={{ background:panel, borderRadius:20, padding:28, width:400, maxWidth:"92vw", boxShadow:"0 20px 60px rgba(0,0,0,0.4)" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
              <h3 style={{ margin:0, color:T.text, fontSize:16, fontWeight:800 }}>یادآور جدید</h3>
              <button onClick={()=>setShowAddAlarm(false)} style={{ background:"none", border:"none", cursor:"pointer", color:T.subtext }}><X size={18}/></button>
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
              <div><Label>عنوان *</Label>{inp(alarmForm.title, v=>setAlarmForm(f=>({...f,title:v})), "عنوان یادآور")}</div>
              <div>
                <Label>تاریخ *</Label>
                <PersianDatePicker value={alarmForm.date} onChange={v=>setAlarmForm(f=>({...f,date:gregorianToJalaliStr(v)}))}/>
              </div>
              <div><Label>ساعت *</Label><input value={alarmForm.time} onChange={e=>setAlarmForm(f=>({...f,time:e.target.value}))} type="time" style={{ width:"100%", padding:"9px 12px", borderRadius:10, border:`1.5px solid ${border}`, background:dark?"rgba(255,255,255,0.05)":"#f8fafc", color:T.text, fontSize:13, fontFamily:"inherit", outline:"none", boxSizing:"border-box" as const }}/></div>
              <div>
                <Label>صدای آلارم</Label>
                <select value={alarmForm.sound} onChange={e=>setAlarmForm(f=>({...f,sound:e.target.value}))} style={{ width:"100%", padding:"9px 12px", borderRadius:10, border:`1.5px solid ${border}`, background:dark?"rgba(255,255,255,0.05)":"#f8fafc", color:T.text, fontSize:13, fontFamily:"inherit", outline:"none" }}>
                  <option value="default">🔔 پیش‌فرض</option>
                  <option value="bell">🛎 زنگ</option>
                  <option value="chime">🎵 ملودی</option>
                  <option value="alert">🚨 هشدار</option>
                  <option value="none">🔕 بدون صدا</option>
                </select>
              </div>
              <div>
                <Label>تکرار</Label>
                <select value={alarmForm.repeat} onChange={e=>setAlarmForm(f=>({...f,repeat:e.target.value}))} style={{ width:"100%", padding:"9px 12px", borderRadius:10, border:`1.5px solid ${border}`, background:dark?"rgba(255,255,255,0.05)":"#f8fafc", color:T.text, fontSize:13, fontFamily:"inherit", outline:"none" }}>
                  <option value="none">بدون تکرار</option>
                  <option value="daily">روزانه</option>
                  <option value="weekly">هفتگی</option>
                  <option value="monthly">ماهانه</option>
                </select>
              </div>
              <div><Label>یادداشت</Label>{inp(alarmForm.note, v=>setAlarmForm(f=>({...f,note:v})), "یادداشت اختیاری", true)}</div>
              <div>
                <Label>تگ کردن کاربران</Label>
                <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                  {users.filter(u=>u.id!==myId).map(u=>{
                    const sel = alarmForm.shared_with.includes(u.id);
                    return <button key={u.id} onClick={()=>setAlarmForm(f=>({...f, shared_with: sel?f.shared_with.filter(id=>id!==u.id):[...f.shared_with,u.id]}))} style={{ padding:"4px 10px", borderRadius:8, border:`1px solid ${sel?"#ef4444":border}`, background:sel?"rgba(239,68,68,0.1)":"transparent", color:sel?"#ef4444":T.text, fontSize:11, cursor:"pointer", fontFamily:"inherit" }}>{u.name}</button>;
                  })}
                </div>
              </div>
              <button onClick={saveAlarm} style={{ padding:"11px", borderRadius:10, border:"none", background:"#ef4444", color:"#fff", fontWeight:700, cursor:"pointer", fontSize:13, fontFamily:"inherit" }}>ثبت یادآور</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}